import {
  PlusCircleOutlined as AddUserIcon,
  LeftOutlined as BackIcon,
  InfoCircleOutlined as InfoIcon,
  RightOutlined as NextIcon,
} from '@ant-design/icons'
import { Button, Checkbox, Col, Divider, Form, Input, Popover, Row, Select, Space, Tooltip, Typography } from 'antd'
import { EMAIL_RULES, toNumber } from '../../util/commonUtil'
import { hideNotification, notifyError, notifyInfo, notifyWarn } from '../../util/notifyUtil'
import { useEffect, useState } from 'react'

import GenderInput from '../common/GenderInput'
import { Link } from 'react-router-dom'
import PublicProfileInput from '../common/PublicProfileInput'
import RegistrationSummary from './RegistrationSummary'
import SkillLevelInput from '../common/SkillLevelInput'
import _ from 'lodash'
import axios from '../../axios/axios'
import { getServerYear } from '../../util/serverTimeUtil'
import { isBlank } from '../../util/stringUtil'
import { transformServerError } from '../../util/errorUtil'

const { Text, Title, Paragraph } = Typography

const interestedInAdultDoublesEnabled = false
const interestedInInterurbanEnabled = true

const getBirthYearRule = () => {
  const currentYear = getServerYear()
  const min = currentYear - 18
  const max = currentYear - 3
  return [
    {
      whitespace: true,
      type: 'number',
      transform: toNumber,
      required: true,
      min,
      max,
      message: `Birth year must be between ${min} and ${max}`,
    },
  ]
}

const colProps = { xs: 24, sm: 12, lg: 6, xl: 6 }
const required = [{ whitespace: true, required: true, message: 'Required' }]

const usernameRule = [
  {
    whitespace: true,
    min: 3,
    message: 'Username must be at least 3 characters',
  },
]
const passwordRule = [
  {
    whitespace: true,
    required: true,
    min: 6,
    message: `Password must be at least 6 characters`,
  },
]

const RegisterStep2Details = ({ registration, setRegistration, onPrevious, onNext }) => {
  const [showForm, setShowForm] = useState()
  const [editing, setEditing] = useState(false)
  const [selectedMemberIndex, setSelectedMemberIndex] = useState(0)
  const [isJunior, setIsJunior] = useState(false)
  const [isAdult, setIsAdult] = useState(false)
  const [passwordRequired, setPasswordRequired] = useState(false)

  const isFirstMember = selectedMemberIndex === 0
  // const isFirstJuniorMember =  _.filter(registration?.members, (m) => m.ageGroup === 'junior').length === 0
  const firstJuniorIndex = _.findIndex(registration.members, { ageGroup: 'junior' })
  const isFirstJuniorMember = firstJuniorIndex < 0 || selectedMemberIndex === firstJuniorIndex
  const firstAdultMember = _.first(registration?.members, (m) => m.ageGroup !== 'junior')

  const validateUsername = async (username) => {
    let usernames = _.map(registration.members, (member) => member.username)
    if (editing) {
      // Exclude this user's username
      usernames.splice(selectedMemberIndex, 1)
    }
    if (_.includes(usernames, username)) {
      return false
    }
    try {
      // Check the server
      await axios.post('/registration/validateUsername', { username }, { noauth: true })
      return true
    } catch (e) {
      const msg = transformServerError(e)
      if (msg.code === 'USERNAME_NOT_AVAILABLE') {
        return false
      } else {
        notifyError('error', e)
      }
    }
  }

  const addOrUpdateMember = async (rawValues) => {
    hideNotification('username-validation')
    // Sanitize
    let {
      username,
      juniorLessonsDay,
      juniorLessonsTime,
      firstName,
      lastName,
      email,
      phone,
      parentFirstName,
      parentLastName,
      parentEmail,
      parentPhone,
      street,
      city,
      postalCode,
      ...rest
    } = rawValues

    const sanitizedValues = {
      username: _.trim(_.toLower(username)),
      firstName,
      lastName,
      email,
      phone,
      ...rest,
      juniorLessonsDay: _.isEmpty(juniorLessonsDay) ? undefined : juniorLessonsDay,
      juniorLessonsTime: _.isEmpty(juniorLessonsTime) ? undefined : juniorLessonsTime,
    }

    if (sanitizedValues.username) {
      const validUsername = await validateUsername(sanitizedValues.username)
      if (!validUsername) {
        notifyError(
          'username-validation',
          'Username not available',
          `Sorry, the username "${sanitizedValues.username}" is already taken.`,
        )
        return
      }
    }

    const regFirstName = parentFirstName ?? firstName
    const regLastName = parentLastName ?? lastName
    const regEmail = parentEmail ?? email
    const regPhone = parentPhone ?? phone

    let members
    if (editing) {
      members = [
        ...registration.members.slice(0, selectedMemberIndex),
        { ...sanitizedValues },
        ...registration.members.slice(selectedMemberIndex + 1),
      ]
    } else {
      members = [...registration.members, { ...sanitizedValues }]
    }

    const reg =
      isFirstMember || isFirstJuniorMember
        ? {
            ...registration,
            firstName: regFirstName,
            lastName: regLastName,
            email: regEmail,
            phone: regPhone,
            street,
            city,
            postalCode,
          }
        : registration

    setRegistration({ ...reg, members })
    setShowForm(false)
    setSelectedMemberIndex(null)
  }

  const handlePersonalInfoChanged = (a, b) => {
    if (!isJunior) {
      return
    }

    const { parentLastName, parentEmail, parentPhone, lastName, email, phone } = form.getFieldsValue()

    const updates = {}
    if (isBlank(lastName)) updates.lastName = parentLastName
    if (isBlank(email)) updates.email = parentEmail
    if (isBlank(phone)) updates.phone = parentPhone
    form.setFieldsValue(updates)
  }

  const setAgeGroupOptions = (ageGroup) => {
    const isJunior = ageGroup === 'junior'
    setIsJunior(isJunior)
    setIsAdult(!isJunior)
    if (!isJunior) {
      form.setFieldsValue({ birthYear: undefined, juniorLessonsDay: undefined, juniorLessonsTime: undefined })
    }
  }

  const editMember = (i) => {
    let {
      email: parentEmail,
      phone: parentPhone,
      firstName: parentFirstName,
      lastName: parentLastName,
    } = registration ?? {}
    const {
      firstName: firstAdultFirstName,
      lastName: firstAdultLastName,
      email: firstAdultEmail,
      phone: firstAdultPhone,
    } = firstAdultMember ?? {}
    parentEmail = parentEmail ?? firstAdultEmail
    parentPhone = parentPhone ?? firstAdultPhone
    parentFirstName = parentFirstName ?? firstAdultFirstName
    parentLastName = parentLastName ?? firstAdultLastName
    form.setFieldsValue({
      ...registration,
      parentFirstName,
      parentLastName,
      parentPhone,
      parentEmail,
      ...registration.members[i],
    })
    setSelectedMemberIndex(i)
    setAgeGroupOptions(registration.members[i].ageGroup)
    setEditing(true)
    setPasswordRequired(false)
    setShowForm(true)
  }

  const [form] = Form.useForm()

  const addMember = () => {
    let {
      email: parentEmail,
      phone: parentPhone,
      firstName: parentFirstName,
      lastName: parentLastName,
    } = registration ?? {}
    const {
      firstName: firstAdultFirstName,
      lastName: firstAdultLastName,
      email: firstAdultEmail,
      phone: firstAdultPhone,
    } = firstAdultMember ?? {}
    parentEmail = parentEmail ?? firstAdultEmail
    parentPhone = parentPhone ?? firstAdultPhone
    parentFirstName = parentFirstName ?? firstAdultFirstName
    parentLastName = parentLastName ?? firstAdultLastName
    form.setFieldsValue({ ...defaults, parentFirstName, parentLastName, parentPhone, parentEmail })
    setEditing(false)
    setPasswordRequired(false)
    setShowForm(true)
    setIsAdult(false)
    setIsJunior(false)
  }

  const cancel = () => {
    setShowForm(false)
    setEditing(false)
    setPasswordRequired(false)
    setSelectedMemberIndex(null)
  }

  const memberCount = _.size(registration.members)

  useEffect(() => {
    setShowForm(memberCount === 0)
  }, [memberCount])

  const { lastName, email, phone } = registration
  const defaults = {
    username: null,
    password: null,
    firstName: null,
    skill: null,
    ageGroup: null,
    gender: null,
    interestedInAdultDoubles: false,
    interestedInInterurban: false,
    lastName,
    email,
    phone,
    isProfilePublic: true,
  }

  const ageGroupOptions = [
    { value: 'adult', name: 'Adult', description: '18 or older' },
    { value: 'junior', name: 'Junior', description: 'Under 18' },
    { value: 'senior', name: 'Senior', description: '60 or older' },
    { value: 'student', name: 'Student', description: '18+ full-time student' },
  ]

  const usernameChanged = (e) => {
    const hasUsername = !!e.target?.value
    setPasswordRequired(hasUsername)
    if (!hasUsername) {
      form.setFields([{ name: 'password', errors: [] }])
      form.setFieldsValue({ password: undefined })
    }
  }

  const addressInfoTooltip = isFirstMember ? '' : 'All members on a single registration must live at the same address'
  const juniorInfoTooltip = isFirstJuniorMember
    ? ''
    : 'All junior members share the same parent/guardian info.  Update the first junior member to change these values.'

  const firstNameLabel = isJunior ? "Child's first name" : 'First name'
  const lastNameLabel = isJunior ? "Child's last name" : 'Last name'
  const emailLabel = isJunior ? "Child's email" : 'Email'
  const phoneLabel = isJunior ? "Child's phone" : 'Phone'

  return (
    <>
      <Paragraph>Provide details for each member linked to this registration.</Paragraph>
      <RegistrationSummary
        registration={registration}
        setRegistration={setRegistration}
        editMember={editMember}
        selectedMemberIndex={selectedMemberIndex}
      />
      <Form
        name="registration-details"
        layout="vertical"
        size="small"
        form={form}
        onFinish={addOrUpdateMember}
        initialValues={defaults}
        autoComplete="off"
      >
        {showForm && (
          <div className="registrationMemberForm">
            <Title style={{ marginTop: '0' }} level={3}>
              Member #{editing ? selectedMemberIndex + 1 : memberCount + 1}
            </Title>
            <Row gutter={10}>
              <Col {...colProps}>
                <Form.Item label="Age group" name="ageGroup" rules={required}>
                  <Select autoFocus dropdownMatchSelectWidth={false} onChange={setAgeGroupOptions}>
                    {ageGroupOptions.map(({ value, name, description }) => (
                      <Select.Option key={value}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          <span>{name}</span>
                          <Text italic type="secondary" style={{ fontSize: '13px', marginLeft: '16px' }}>
                            {description}
                          </Text>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {isJunior && (
              <Row gutter={10}>
                <Col {...colProps}>
                  <Form.Item
                    label="Parent / guardian's first name"
                    name="parentFirstName"
                    rules={required}
                    tooltip={juniorInfoTooltip}
                  >
                    <Input disabled={!isFirstJuniorMember} />
                  </Form.Item>
                </Col>
                <Col {...colProps}>
                  <Form.Item
                    label="Parent / guardian's last name"
                    name="parentLastName"
                    rules={required}
                    tooltip={juniorInfoTooltip}
                  >
                    <Input onBlur={handlePersonalInfoChanged} disabled={!isFirstJuniorMember} />
                  </Form.Item>
                </Col>
                <Col {...colProps}>
                  <Form.Item
                    label="Parent / guardian's email"
                    name="parentEmail"
                    rules={EMAIL_RULES}
                    tooltip={juniorInfoTooltip}
                  >
                    <Input onBlur={handlePersonalInfoChanged} disabled={!isFirstJuniorMember} />
                  </Form.Item>
                </Col>
                <Col {...colProps}>
                  <Form.Item
                    label="Parent / guardian's phone"
                    name="parentPhone"
                    rules={required}
                    tooltip={juniorInfoTooltip}
                  >
                    <Input onBlur={handlePersonalInfoChanged} disabled={!isFirstJuniorMember} />
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={10}>
              <Col {...colProps}>
                <Form.Item label={firstNameLabel} name="firstName" rules={required}>
                  <Input />
                </Form.Item>
              </Col>
              <Col {...colProps}>
                <Form.Item label={lastNameLabel} name="lastName" rules={required}>
                  <Input />
                </Form.Item>
              </Col>
              <Col {...colProps}>
                <Form.Item label={emailLabel} name="email" rules={EMAIL_RULES}>
                  <Input />
                </Form.Item>
              </Col>
              <Col {...colProps}>
                <Form.Item label={phoneLabel} name="phone" rules={required}>
                  <Input />
                </Form.Item>
              </Col>

              <Col {...colProps}>
                <Form.Item label="Street" name="street" rules={required} tooltip={addressInfoTooltip}>
                  <Input disabled={!isFirstMember} />
                </Form.Item>
              </Col>
              <Col {...colProps}>
                <Form.Item label="City" name="city" rules={required} tooltip={addressInfoTooltip}>
                  <Input disabled={!isFirstMember} />
                </Form.Item>
              </Col>
              <Col {...colProps}>
                <Form.Item label="Postal code" name="postalCode" rules={required} tooltip={addressInfoTooltip}>
                  <Input style={{ textTransform: 'uppercase' }} disabled={!isFirstMember} />
                </Form.Item>
              </Col>
              <div style={{ flexBasis: '100%', height: 0 }} />
              <Col {...colProps}>
                <SkillLevelInput />
              </Col>
              <Col {...colProps}>
                <GenderInput />
              </Col>
            </Row>
            {isJunior && (
              <Row gutter={10}>
                <Col span={24}>
                  <Paragraph className="note" style={{ marginTop: '2em', marginBottom: '0' }}>
                    Spring lessons are offered for free for interested junior members. Lessons are one session per week,
                    for 6 weeks.
                  </Paragraph>
                </Col>
                <Col {...colProps}>
                  <Form.Item label="Birth year" name="birthYear" rules={getBirthYearRule()}>
                    <Input />
                  </Form.Item>
                </Col>
                <Col {...colProps}>
                  <Form.Item label="Spring lessons day" name="juniorLessonsDay">
                    <Select allowClear>
                      <Select.Option key="">Not interested</Select.Option>
                      <Select.Option key="tuesday">Tuesdays</Select.Option>
                      <Select.Option key="wednesday">Wednesdays</Select.Option>
                      <Select.Option key="thursday">Thursdays</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col {...colProps}>
                  <Form.Item label="Spring lessons time" name="juniorLessonsTime">
                    <Select allowClear>
                      <Select.Option key="">Not interested</Select.Option>
                      <Select.Option key="_4pm">4:30 - 5:30pm</Select.Option>
                      <Select.Option key="_5pm">5:30 - 6:30pm</Select.Option>
                      <Select.Option key="noPreference">No preference</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )}

            {isAdult && (interestedInAdultDoublesEnabled || interestedInInterurbanEnabled) && (
              <Row gutter={10}>
                <Col span={24}>
                  <Paragraph className="note" style={{ marginTop: '2em', marginBottom: '1em' }}>
                    Let us know if you might be interested in joining the following programs. We use this to guage
                    interest which will help us plan the year. <br />
                    More information about these programs and how to sign up will be sent out early in the season.
                  </Paragraph>
                </Col>
                {interestedInAdultDoublesEnabled && (
                  <Col span={24}>
                    <Form.Item label="" noStyle name="interestedInAdultDoubles" valuePropName="checked">
                      <Checkbox>I'm interested in playing in the Adult Social Doubles league.</Checkbox>
                    </Form.Item>
                    <Popover
                      overlayStyle={{ maxWidth: '30em' }}
                      placement="right"
                      content={
                        <Paragraph>
                          This is a social doubles league with various divisions based on skill level. See{' '}
                          <Link to="/adult-doubles" target="_blank">
                            Adult Social Doubles
                          </Link>{' '}
                          for more information.
                        </Paragraph>
                      }
                    >
                      <InfoIcon />
                    </Popover>
                  </Col>
                )}
                {interestedInInterurbanEnabled && (
                  <Col span={24}>
                    <Form.Item label="" noStyle name="interestedInInterurban" valuePropName="checked">
                      <Checkbox>I'm interested in trying out for the Interurban doubles team.</Checkbox>
                    </Form.Item>
                    <Popover
                      overlayStyle={{ maxWidth: '30em' }}
                      placement="right"
                      content={
                        <Paragraph>
                          This is a competitive doubles travel team for advanced players that plays other teams in the
                          region.
                        </Paragraph>
                      }
                    >
                      <InfoIcon />
                    </Popover>
                  </Col>
                )}
              </Row>
            )}

            <Row gutter={10}>
              <Col span={24}>
                <Paragraph className="" style={{ marginTop: '2em', marginBottom: '0' }}>
                  <b>Please select a username and password for this user.</b>
                </Paragraph>
                <Paragraph className="note">
                  Leave this blank only if this user will never need to book a court. This is your{' '}
                  <b>only opportunity</b> to select a username and password for this user.
                </Paragraph>
              </Col>
              <Col {...colProps}>
                <Form.Item label="Username" name="username" onChange={usernameChanged} rules={usernameRule}>
                  <Input style={{ textTransform: 'lowercase' }} autoComplete="new-username" />
                </Form.Item>
              </Col>
              <Col {...colProps}>
                <Form.Item label="Password" name="password" rules={passwordRequired ? passwordRule : []}>
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
              </Col>
              <Col span={24} style={{ paddingTop: '1.5em' }}>
                <PublicProfileInput editing fullwidth />
              </Col>
            </Row>
            <Row justify="end">
              <Col>
                <Space direction="horizontal">
                  <Button htmlType="submit" type="primary" size="middle">
                    {editing ? 'Save changes' : 'Save new member'}
                  </Button>
                  {memberCount > 0 && (
                    <Button type="link" onClick={cancel}>
                      Cancel
                    </Button>
                  )}
                </Space>
              </Col>
            </Row>
          </div>
        )}

        <Row justify="space-between" style={{ paddingTop: '2em' }}>
          <Col>
            {/* <Button icon={<BackIcon />} onClick={onPrevious} size="large" disabled={memberCount === 0 || showForm}>
              Back
            </Button> */}
          </Col>
          <Col>
            <Space direction="horizontal">
              {!showForm && memberCount > 0 && (
                <>
                  <Button type="primary" size="large" onClick={addMember} style={{ marginTop: '0em' }}>
                    Add another member
                  </Button>
                  or
                </>
              )}
              <Form.Item noStyle>
                <Button
                  icon={<NextIcon />}
                  type="primary"
                  size="large"
                  onClick={onNext}
                  disabled={memberCount === 0 || showForm}
                >
                  Review & Pay
                </Button>
              </Form.Item>
            </Space>
          </Col>
        </Row>
      </Form>
    </>
  )
}

export default RegisterStep2Details
